import { DEFAULT_SECURITY_PROFILES } from './constants.js';
import { runtimeDeprecationService } from '../../../shared/services/runtimeDeprecationService.js';

// Helper function to find flows associated with Lambda functions
const findLambdaAssociatedFlows = (exportedData, functionArn) => {
  const associatedFlows = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    // Search contact flows
    if (instanceData.contactFlows) {
      instanceData.contactFlows.forEach(flow => {
        if (flow.Content && flow.Name) {
          try {
            // Search for Lambda function ARN in the content
            if (flow.Content.includes(`"LambdaFunctionARN":"${functionArn}"`)) {
              associatedFlows.push(flow.Name);
            }
          } catch (error) {
            // Ignore parsing errors
          }
        }
      });
    }
    
    // Search contact flow modules
    if (instanceData.contactFlowModules) {
      instanceData.contactFlowModules.forEach(module => {
        if (module.Content && module.Name) {
          try {
            // Search for Lambda function ARN in the content
            if (module.Content.includes(`"LambdaFunctionARN":"${functionArn}"`)) {
              associatedFlows.push(module.Name);
            }
          } catch (error) {
            // Ignore parsing errors
          }
        }
      });
    }
  });
  
  // Remove duplicates and return comma-delimited string
  const uniqueFlows = [...new Set(associatedFlows)];
  return uniqueFlows.length > 0 ? uniqueFlows.join(', ') : 'N/A';
};

// Helper function to find flows associated with Lex V2 bots
const findLexV2AssociatedFlows = (exportedData, aliasArn) => {
  const associatedFlows = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    // Search contact flows
    if (instanceData.contactFlows) {
      instanceData.contactFlows.forEach(flow => {
        if (flow.Content && flow.Name) {
          try {
            // Search for Lex V2 alias ARN in the content
            if (flow.Content.includes(`"AliasArn":"${aliasArn}"`)) {
              associatedFlows.push(flow.Name);
            }
          } catch (error) {
            // Ignore parsing errors
          }
        }
      });
    }
    
    // Search contact flow modules
    if (instanceData.contactFlowModules) {
      instanceData.contactFlowModules.forEach(module => {
        if (module.Content && module.Name) {
          try {
            // Search for Lex V2 alias ARN in the content
            if (module.Content.includes(`"AliasArn":"${aliasArn}"`)) {
              associatedFlows.push(module.Name);
            }
          } catch (error) {
            // Ignore parsing errors
          }
        }
      });
    }
  });
  
  // Remove duplicates and return comma-delimited string
  const uniqueFlows = [...new Set(associatedFlows)];
  return uniqueFlows.length > 0 ? uniqueFlows.join(', ') : 'N/A';
};

export const mapHoursOfOperation = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.hoursOfOperation) {
      instanceData.hoursOfOperation.forEach(hours => {
        // Format the hours of operation string
        const hoursString = formatHoursString(hours.Config);
        
        mappedData.push({
          name: hours.Name || '',
          description: hours.Description || '',
          hoursOfOperation: hoursString,
          timeZone: hours.TimeZone || ''
        });
      });
    }
  });
  
  return mappedData;
};

export const mapDialedNumberMappings = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    const contactFlows = instanceData.contactFlows || [];

    if (instanceData.phoneNumbers) {
      instanceData.phoneNumbers.forEach(phoneNumber => {
        const matchingFlow = contactFlows.find(
          flow => flow.Arn === phoneNumber.FlowId
        );

        mappedData.push({
          phoneNumber: phoneNumber.PhoneNumber || '',
          description: phoneNumber.PhoneNumberDescription || '',
          type: phoneNumber.PhoneNumberType || '',
          countryCode: phoneNumber.PhoneNumberCountryCode || '',
          targetArn: phoneNumber.TargetArn || '',
          flowId: phoneNumber.FlowId || '',
          flowName: matchingFlow?.Name || 'N/A'
        });
      });
    }
  });
  
  return mappedData;
};

export const mapAgentStatus = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.agentStatuses) {
      instanceData.agentStatuses.forEach(agentStatus => {
        mappedData.push({
          name: agentStatus.Name || '',
          description: agentStatus.Description || '',
          type: agentStatus.Type || '',
          state: agentStatus.State || '',
          displayOrder: agentStatus.DisplayOrder || '',
          arn: agentStatus.Arn || ''
        });
      });
    }
  });
  
  return mappedData;
};

export const mapQueues = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    const hoursOfOperation = instanceData.hoursOfOperation || [];
    const contactFlows = instanceData.contactFlows || [];
    const phoneNumbers = instanceData.phoneNumbers || [];

    if (instanceData.queues) {
      instanceData.queues.forEach(queue => {

        const matchingHours = hoursOfOperation.find(
          hours => hours.Id === queue.HoursOfOperationId
        );

        const outboundCallerName = queue.OutboundCallerConfig?.OutboundCallerIdName || '';
        const outboundCallerNumberId = queue.OutboundCallerConfig?.OutboundCallerIdNumberId || '';
        const outboundFlowId = queue.OutboundCallerConfig?.OutboundFlowId || '';
        const maxContacts = queue?.MaxContacts || 0;

        const matchingFlow = outboundFlowId
            ? contactFlows.find(flow => flow.Id === outboundFlowId)
            : {};

        const matchingPhoneNumber = outboundCallerNumberId
            ?  phoneNumbers.find(phoneNumbers => phoneNumbers.PhoneNumberId === outboundCallerNumberId)
            : {};
        
        mappedData.push({
          name: queue.Name || '',
          description: queue.Description || '',
          type: queue.QueueType || '',
          status: queue.Status || '',
          hoursOfOperationId: queue.HoursOfOperationId || '',
          hoursOfOperationName: matchingHours?.Name || 'N/A',
          outboundCallerIdName: outboundCallerName,
          outboundCallerIdNumber: outboundCallerNumberId || 'N/A',
          outboundCallerNumber: matchingPhoneNumber?.PhoneNumber,
          outboundFlowId: outboundFlowId,
          outboundFlowName: matchingFlow?.Name || 'N/A',
          maxContacts: maxContacts,
          arn: queue.QueueArn || queue.Arn || ''
        });
      });
    }
  });
  
  return mappedData;
};

export const mapQuickConnects = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    const contactFlows = instanceData.contactFlows || [];
    const queues = instanceData.queues || [];
    const users = instanceData.users || [];

    if (instanceData.quickConnects) {

      instanceData.quickConnects.forEach(quickConnect => {
        // Extract target ID based on type
        let targetId = '';
        let contactFlowId = '';
        let targetName = '';
        let contactFlowName = '';
        
        if (quickConnect.QuickConnectConfig) {
          const config = quickConnect.QuickConnectConfig;
          if (config.UserConfig) {
            targetId = config.UserConfig.UserId || '';
            const matchingUser = targetId
                ? users.find(user => user.Id === targetId)
                : {};
            targetName = matchingUser?.Username || 'N/A'

            contactFlowId = config.UserConfig.ContactFlowId || '';
            const matchingFlow = contactFlowId
                ? contactFlows.find(flow => flow.Id === contactFlowId)
                : {};
            contactFlowName = matchingFlow?.Name || 'N/A'
          } else if (config.QueueConfig) {
            targetId = config.QueueConfig.QueueId || '';
            const matchingQueue = targetId
                ? queues.find(queue => queue.Id === targetId)
                : {};
            targetName = matchingQueue?.Name || 'N/A'

            contactFlowId = config.QueueConfig.ContactFlowId || '';
            const matchingFlow = contactFlowId
                ? contactFlows.find(flow => flow.Id === contactFlowId)
                : {};
            contactFlowName = matchingFlow?.Name || 'N/A'
          } else if (config.PhoneConfig) {
            targetId = config.PhoneConfig.PhoneNumber || '';
            // For PHONE_NUMBER type, the phone number is directly in the config - no lookup needed
            targetName = config.PhoneConfig.PhoneNumber || 'N/A'
          }
        }
        
        mappedData.push({
          name: quickConnect.Name || '',
          description: quickConnect.Description || '',
          type: quickConnect.QuickConnectType || '',
          targetId: targetId,
          targetName: targetName,
          contactFlowId: contactFlowId,
          contactFlowName: contactFlowName,
          arn: quickConnect.QuickConnectARN || quickConnect.Arn || ''
        });
      });
    }
  });
  
  return mappedData;
};

export const mapRoutingProfiles = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    const queues = instanceData.queues || [];

    if (instanceData.routingProfiles) {
      instanceData.routingProfiles.forEach(routingProfile => {
        // Extract channels and concurrency information
        let channels = '';
        let chatConcurrency = '';
        let taskConcurrency = '';
        let emailConcurrency = '';
        let outboundQueueName = '';
        
        if (routingProfile.MediaConcurrencies) {
          // Get all channel names for the Channels column
          channels = routingProfile.MediaConcurrencies
            .map(mc => mc.Channel)
            .join(', ');
          
          // Find specific channel concurrencies
          routingProfile.MediaConcurrencies.forEach(mc => {
            const concurrencyText = `${mc.Concurrency} - ${mc.CrossChannelBehavior?.BehaviorType || 'N/A'}`;
            
            switch (mc.Channel) {
              case 'CHAT':
                chatConcurrency = concurrencyText;
                break;
              case 'TASK':
                taskConcurrency = concurrencyText;
                break;
              case 'EMAIL':
                emailConcurrency = concurrencyText;
                break;
              default:
                // Handle any other channel types that might exist
                break;
            }
          });
        }
        
        // Format associated queues (use pipe delimiter to support queue names containing commas)
        const associatedQueues = routingProfile.RoutingProfileQueueConfigSummaryList
          ? routingProfile.RoutingProfileQueueConfigSummaryList.map(queue =>
              `${queue.QueueName} (P:${queue.Priority}, D:${queue.Delay})`
            ).join(' | ')
          : '';

        const matchingQueue = routingProfile.DefaultOutboundQueueId
            ? queues.find(queue => queue.Id === routingProfile.DefaultOutboundQueueId)
            : {};
        outboundQueueName = matchingQueue?.Name || 'N/A'
        
        mappedData.push({
          name: routingProfile.Name || '',
          description: routingProfile.Description || '',
          channels: channels,
          chatConcurrency: chatConcurrency,
          taskConcurrency: taskConcurrency,
          emailConcurrency: emailConcurrency,
          defaultOutboundQueue: outboundQueueName || '',
          agentAvailabilityTimer: routingProfile.AgentAvailabilityTimer || '',
          associatedQueues: associatedQueues,
          isDefault: routingProfile.IsDefault ? 'Yes' : 'No',
          arn: routingProfile.RoutingProfileArn || routingProfile.Arn || ''
        });
      });
    }
  });
  
  return mappedData;
};

export const mapUserHierarchyData = (exportedData) => {
  const mappedData = {
    hierarchyStructure: [],
    groupedHierarchy: []
  };
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.userHierarchyGroups) {
      const { userHierarchyGroups, hierarchyStructure } = instanceData.userHierarchyGroups;
      
      // Extract hierarchy structure level names (for row 5)
      const levelNames = [];
      for (let i = 1; i <= 5; i++) {
        const levelKey = getLevelKey(i);
        if (hierarchyStructure && hierarchyStructure[levelKey]) {
          levelNames.push(hierarchyStructure[levelKey].Name);
        } else {
          levelNames.push('N/A');
        }
      }
      mappedData.hierarchyStructure = levelNames;
      
      // Process user hierarchy groups into a tree structure
      if (userHierarchyGroups && userHierarchyGroups.length > 0) {
        const groupedRows = createGroupedHierarchyRows(userHierarchyGroups);
        mappedData.groupedHierarchy = groupedRows;
      }
    }
  });
  
  return mappedData;
};

export const mapSecurityProfiles = (exportedData) => {
  const mappedData = {
    profiles: [],
    orderedProfiles: []
  };
  
  // Collect all security profiles from all instances
  const allProfiles = [];
  
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.securityProfiles) {
      instanceData.securityProfiles.forEach(profile => {
        allProfiles.push({
          name: profile.Name || '',
          description: profile.Description || '',
          permissions: profile.Permissions || [],
          arn: profile.Arn || ''
        });
      });
    }
  });
  
  // Create a map for quick lookup
  const profileMap = {};
  allProfiles.forEach(profile => {
    profileMap[profile.name] = profile;
  });
  
  // Order profiles: default profiles first, then additional ones
  const orderedProfiles = [];
  
  // Add default profiles in order (if they exist)
  DEFAULT_SECURITY_PROFILES.forEach(defaultProfileName => {
    if (profileMap[defaultProfileName]) {
      orderedProfiles.push(profileMap[defaultProfileName]);
      delete profileMap[defaultProfileName]; // Remove from map so we don't add it again
    }
  });
  
  // Add remaining profiles
  Object.values(profileMap).forEach(profile => {
    orderedProfiles.push(profile);
  });
  
  mappedData.profiles = allProfiles;
  mappedData.orderedProfiles = orderedProfiles;
  
  return mappedData;
};

export const mapLambdaFunctions = async (exportedData) => {
  const mappedData = [];
  
  // Get runtime deprecation data once for all functions
  console.log('Fetching runtime deprecation data for Lambda functions...');
  
  // Iterate through each instance (or in this case, each region/account)
  // eslint-disable-next-line no-unused-vars
  for (const [instanceId, instanceData] of Object.entries(exportedData)) {
    if (instanceData.functions) {
      for (const func of instanceData.functions) {
        // Get data options from function (set in fetchLambdaFunctions)
        const dataOptions = func._dataOptions || {
          includeRuntimeDeprecation: true,
          includeLogRetention: true,
          includeLastInvocation: true,
          includeInvocationCounts: true
        };

        // Format the LastModified date to be more readable
        const lastModified = func.LastModified 
          ? new Date(func.LastModified).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })
          : '';

        // Use the contextual last invocation display that includes scan depth context
        const lastInvocation = func.LastInvocationDisplay || 'Unknown';

        // Get runtime deprecation status if enabled
        let blockFunctionCreate = 'Not Collected';
        let blockFunctionUpdate = 'Not Collected';
        
        if (dataOptions.includeRuntimeDeprecation && func.Runtime) {
          try {
            const runtimeStatus = await runtimeDeprecationService.getRuntimeStatus(func.Runtime);
            blockFunctionCreate = runtimeStatus.blockFunctionCreate;
            blockFunctionUpdate = runtimeStatus.blockFunctionUpdate;
          } catch (error) {
            console.warn(`Failed to get runtime status for ${func.FunctionName} (${func.Runtime}):`, error);
            blockFunctionCreate = 'Unknown';
            blockFunctionUpdate = 'Unknown';
          }
        }

        mappedData.push({
          functionName: func.FunctionName || '',
          description: func.Description || '',
          runtime: func.Runtime || '',
          lastModified: lastModified,
          logRetention: func.LogGroupRetention || 'Unknown',
          lastInvocation: lastInvocation,
          blockFunctionCreate: blockFunctionCreate,
          blockFunctionUpdate: blockFunctionUpdate,
          invocations30Days: func.Invocations30Days || 0,
          errors30Days: func.Errors30Days || 0,
          version: func.Version || '',
          codeSize: func.CodeSize || 0,
          timeout: func.Timeout || 0,
          memorySize: func.MemorySize || 0,
          handler: func.Handler || '',
          role: func.Role || '',
          functionArn: func.FunctionArn || '',
          logGroupRetentionDays: func.LogGroupRetentionDays || null,
          logGroupArn: func.LogGroupArn || '',
          logGroupStoredBytes: func.LogGroupStoredBytes || 0,
          lastInvocationTimestamp: func.LastInvocation || null,
          maxDaysScanned: func.MaxDaysScanned || 0,
          // Store data options for Excel export
          _dataOptions: dataOptions
        });
      }
    }
  }
  
  console.log(`Processed ${mappedData.length} Lambda functions with conditional data collection`);
  return mappedData;
};

export const mapConnectLambdaFunctions = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    // Check if we have both Lambda functions and instance config
    if (instanceData.functions && instanceData.instanceConfig && instanceData.instanceConfig.lambdaFunctions) {
      // Get the list of Lambda ARNs associated with this Connect instance
      const associatedLambdaArns = new Set(instanceData.instanceConfig.lambdaFunctions || []);
      
      // Filter and map the functions that are associated with this Connect instance
      instanceData.functions
        .filter(func => associatedLambdaArns.has(func.FunctionArn))
        .forEach(func => {
          // Find associated flows for this Lambda function
          const associatedFlows = findLambdaAssociatedFlows(exportedData, func.FunctionArn);
          
          mappedData.push({
            functionName: func.FunctionName || '',
            description: func.Description || '',
            associatedFlows: associatedFlows
          });
        });
    }
  });
  
  return mappedData;
};

export const mapLexBots = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    // Collect all connected Lex V2 bot alias ARNs
    const connectedV2BotAliasArns = new Set();

    // Check instanceConfig.lexBots for connected bots
    if (instanceData.instanceConfig && instanceData.instanceConfig.lexBots) {
      console.log(`Processing ${instanceData.instanceConfig.lexBots.length} associated Lex bots for instance ${instanceId}`);

      instanceData.instanceConfig.lexBots.forEach(lexBot => {
        // V2 bots have LexV2Bot property
        if (lexBot.LexV2Bot && lexBot.LexV2Bot.AliasArn) {
          connectedV2BotAliasArns.add(lexBot.LexV2Bot.AliasArn);
          console.log(`Found connected V2 bot alias: ${lexBot.LexV2Bot.AliasArn}`);
        }
      });
    }

    console.log(`Instance ${instanceId} - Connected V2 bot aliases: ${connectedV2BotAliasArns.size}`);
    
    // Process Lex V2 bots
    if (instanceData.lexBotV2) {
      console.log(`Processing ${instanceData.lexBotV2.length} V2 bots for instance ${instanceId}`);
      
      instanceData.lexBotV2.forEach(bot => {
        // Get the list of supported languages from locales
        const languages = bot.locales 
          ? bot.locales.map(locale => locale.localeId).join(', ')
          : '';
        
        // Extract COPPA compliance status
        const isCoppa = bot.childDirected || 
                       (bot.dataPrivacy && bot.dataPrivacy.childDirected) || 
                       false;
        
        // Extract idle session timeout in seconds
        const idleSessionTimeout = bot.idleSessionTTLInSeconds 
          ? `${bot.idleSessionTTLInSeconds} seconds`
          : 'N/A';
        
        // Format aliases with their versions and check connection status
        let aliasVersionText = '';
        let connectedStatusText = '';
        let associatedFlowsText = '';
        
        if (bot.aliases && bot.aliases.length > 0) {
          // Process each alias
          bot.aliases.forEach((alias, index) => {
            // Format as "AliasName:Version"
            const aliasVersionLine = `${alias.botAliasName}:${alias.botVersion}`;
            
            // Add newline except for the last item
            aliasVersionText += index === bot.aliases.length - 1 
              ? aliasVersionLine 
              : aliasVersionLine + '\n';
            
            // Check if this alias is connected to the Connect instance
            let isConnected = false;
            
            // Extract region and account ID from the bot's role ARN if available
            let region = 'us-west-2'; // Default
            let accountId = '761763126862'; // Default
            
            if (bot.roleArn) {
              const parts = bot.roleArn.split(':');
              if (parts.length >= 5) {
                region = parts[3] || region;
                accountId = parts[4] || accountId;
              }
            }
            
            const constructedAliasArn = `arn:aws:lex:${region}:${accountId}:bot-alias/${bot.botId}/${alias.botAliasId}`;
            isConnected = connectedV2BotAliasArns.has(constructedAliasArn);
            
            // Add Yes/No with newline alignment
            connectedStatusText += index === bot.aliases.length - 1 
              ? (isConnected ? 'Yes' : 'No') 
              : (isConnected ? 'Yes\n' : 'No\n');
            
            // Find associated flows for this alias (only if connected)
            if (isConnected) {
              const flows = findLexV2AssociatedFlows(exportedData, constructedAliasArn);
              associatedFlowsText += index === bot.aliases.length - 1 
                ? flows 
                : flows + '\n';
            } else {
              associatedFlowsText += index === bot.aliases.length - 1 
                ? 'N/A' 
                : 'N/A\n';
            }
          });
        } else {
          aliasVersionText = 'N/A';
          connectedStatusText = 'N/A';
          associatedFlowsText = 'N/A';
        }
        
        // Calculate total intent and slot type counts across all locales
        let totalIntentCount = 0;
        let totalSlotTypeCount = 0;
        
        if (bot.locales && Array.isArray(bot.locales)) {
          bot.locales.forEach(locale => {
            totalIntentCount += locale.intentCount || 0;
            totalSlotTypeCount += locale.slotTypeCount || 0;
          });
        }
        
        let conversationMetrics = 'N/A';
        let utteranceMetrics = 'N/A';
        
        if (bot.conversationAnalytics) {
          const analytics = bot.conversationAnalytics;
          conversationMetrics = `${analytics.totalConversations}/${analytics.droppedConversations}/${analytics.failedConversations}`;
          utteranceMetrics = `${analytics.detectedUtterances}/${analytics.missedUtterances}`;
        }

        mappedData.push({
          botName: bot.botName || '',
          description: bot.description || '',
          subjectToCoppa: isCoppa ? 'Yes' : 'No',
          idleSessionTimeout: idleSessionTimeout,
          languages: languages,
          aliasVersion: aliasVersionText,
          associatedToConnect: connectedStatusText,
          intentCount: totalIntentCount,
          slotTypeCount: totalSlotTypeCount,
          lexVersion: 'V2',
          conversationMetrics: conversationMetrics,
          utteranceMetrics: utteranceMetrics,
          associatedFlows: associatedFlowsText,
          botStatus: bot.botStatus || 'Unknown',
          botType: bot.botType || 'Unknown',
          creationDateTime: bot.creationDateTime ? new Date(bot.creationDateTime).toLocaleString() : 'Unknown',
          lastUpdatedDateTime: bot.lastUpdatedDateTime ? new Date(bot.lastUpdatedDateTime).toLocaleString() : 'Unknown'
        });
      });
    }
    
  });

  console.log(`Total mapped Lex V2 bots: ${mappedData.length}`);
  return mappedData;
};

// Keep the old function name as an alias for backward compatibility
export const mapLexBotsV2 = mapLexBots;

export const mapLexIntents = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.lexBotV2) {
      instanceData.lexBotV2.forEach(bot => {
        // Process each locale
        if (bot.locales && Array.isArray(bot.locales)) {
          bot.locales.forEach(locale => {
            // Process each intent in the locale
            if (locale.intents && Array.isArray(locale.intents)) {
              locale.intents.forEach(intent => {
                // Format sample utterances
                let sampleUtterances = '';
                if (intent.sampleUtterances && Array.isArray(intent.sampleUtterances)) {
                  sampleUtterances = intent.sampleUtterances
                    .map(utterance => utterance.utterance || '')
                    .filter(utterance => utterance.trim() !== '')
                    .join('\n');
                }
                
                // Extract confirmation setting info
                let confirmationSetting = 'None';
                if (intent.intentConfirmationSetting) {
                  const setting = intent.intentConfirmationSetting;
                  if (setting.isActive) {
                    confirmationSetting = 'Active';
                    if (setting.confirmationPrompt && setting.confirmationPrompt.messageGroups) {
                      const messageCount = setting.confirmationPrompt.messageGroups.length;
                      confirmationSetting += ` (${messageCount} prompts)`;
                    }
                  } else {
                    confirmationSetting = 'Inactive';
                  }
                }
                
                // Extract fulfillment type
                let fulfillmentType = 'Unknown';
                if (intent.fulfillmentCodeHook) {
                  fulfillmentType = intent.fulfillmentCodeHook.enabled ? 'Code Hook' : 'Return Intent';
                } else {
                  fulfillmentType = 'Return Intent';
                }
                
                mappedData.push({
                  botName: bot.botName || '',
                  botId: bot.botId || '',
                  localeId: locale.localeId || '',
                  intentName: intent.intentName || '',
                  intentId: intent.intentId || '',
                  description: intent.description || '',
                  sampleUtterances: sampleUtterances,
                  confirmationSetting: confirmationSetting,
                  slotCount: intent.slotCount || 0,
                  fulfillmentType: fulfillmentType,
                  parentIntentSignature: intent.parentIntentSignature || ''
                });
              });
            }
          });
        }
      });
    }
  });
  
  return mappedData;
};

export const mapLexSlotsAndTypes = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.lexBotV2) {
      instanceData.lexBotV2.forEach(bot => {
        // Process each locale
        if (bot.locales && Array.isArray(bot.locales)) {
          bot.locales.forEach(locale => {
            // Create a map of slot types for quick lookup
            const slotTypeMap = {};
            if (locale.slotTypes && Array.isArray(locale.slotTypes)) {
              locale.slotTypes.forEach(slotType => {
                slotTypeMap[slotType.slotTypeId] = slotType;
              });
            }
            
            // Process each intent in the locale
            if (locale.intents && Array.isArray(locale.intents)) {
              locale.intents.forEach(intent => {
                // Process each slot in the intent
                if (intent.slots && Array.isArray(intent.slots)) {
                  intent.slots.forEach(slot => {
                    // Get matching slot type data
                    const slotType = slotTypeMap[slot.slotTypeId] || {};
                    
                    // Extract value elicitation setting info
                    let valueElicitationSetting = 'None';
                    if (slot.valueElicitationSetting) {
                      const setting = slot.valueElicitationSetting;
                      if (setting.slotConstraint) {
                        valueElicitationSetting = setting.slotConstraint;
                      }
                      if (setting.promptSpecification && setting.promptSpecification.messageGroups) {
                        const promptCount = setting.promptSpecification.messageGroups.length;
                        valueElicitationSetting += ` (${promptCount} prompts)`;
                      }
                    }
                    
                    // Determine if slot is required
                    const isRequired = slot.valueElicitationSetting && 
                                     slot.valueElicitationSetting.slotConstraint === 'Required' ? 'Yes' : 'No';
                    
                    // Format slot type values
                    let slotTypeValues = '';
                    if (slotType.slotTypeValues && Array.isArray(slotType.slotTypeValues)) {
                      slotTypeValues = slotType.slotTypeValues
                        .map(value => {
                          if (value.sampleValue && value.sampleValue.value) {
                            let displayValue = value.sampleValue.value;
                            // Add synonyms if available
                            if (value.synonyms && Array.isArray(value.synonyms) && value.synonyms.length > 0) {
                              const synonymList = value.synonyms.map(syn => syn.value).join(', ');
                              displayValue += ` (synonyms: ${synonymList})`;
                            }
                            return displayValue;
                          }
                          return '';
                        })
                        .filter(value => value.trim() !== '')
                        .join('\n');
                    }
                    
                    // Format external source setting
                    let externalSourceSetting = '';
                    if (slotType.externalSourceSetting) {
                      const setting = slotType.externalSourceSetting;
                      if (setting.grammarSlotTypeSetting) {
                        externalSourceSetting = `Grammar: ${setting.grammarSlotTypeSetting.source || 'Unknown'}`;
                      }
                    }
                    
                    mappedData.push({
                      botName: bot.botName || '',
                      localeId: locale.localeId || '',
                      intentName: intent.intentName || '',
                      slotName: slot.slotName || '',
                      slotTypeId: slot.slotTypeId || '',
                      slotTypeName: slotType.slotTypeName || slot.slotTypeId || '',
                      slotDescription: slot.description || '',
                      slotTypeDescription: slotType.description || '',
                      slotTypeValues: slotTypeValues,
                      valueSelectionStrategy: slotType.valueSelectionStrategy || 'ORIGINAL_VALUE',
                      valueElicitationSetting: valueElicitationSetting,
                      required: isRequired,
                      parentSlotTypeSignature: slotType.parentSlotTypeSignature || '',
                      externalSourceSetting: externalSourceSetting
                    });
                  });
                }
              });
            }
          });
        }
      });
    }
  });
  
  return mappedData;
};

export const mapContactFlows = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.contactFlows) {
      instanceData.contactFlows.forEach(contactFlow => {
        mappedData.push({
          name: contactFlow.Name || '',
          state: contactFlow.ContactFlowState || '',
          status: contactFlow.ContactFlowStatus || '',
          type: contactFlow.ContactFlowType || '',
          isDefault: contactFlow.IsDefault ? 'Yes' : 'No',
          contactFlowErrors: contactFlow.ContactFlowErrors30Days || 0
        });
      });
    }
  });
  
  return mappedData;
};

export const mapContactFlowModules = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.contactFlowModules) {
      instanceData.contactFlowModules.forEach(contactFlowModule => {
        mappedData.push({
          name: contactFlowModule.Name || '',
          state: contactFlowModule.ContactFlowModuleState || '',
          status: contactFlowModule.ContactFlowModuleStatus || '',
          type: 'Module', // Always 'Module' for flow modules
          isDefault: 'No', // Modules are never default
          contactFlowErrors: contactFlowModule.ContactFlowErrors30Days || 0
        });
      });
    }
  });
  
  return mappedData;
};

export const mapInstanceConfiguration = (exportedData) => {
  const mappedData = [];
  
  // Process each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    if (instanceData.instanceConfig) {
      const config = instanceData.instanceConfig;
      const details = config.instanceDetails || {};
      const attributes = config.attributes || [];
      const storageConfigs = config.storageConfigs || [];
      const integrations = config.integrationAssociations || [];

      // Helper function to get attribute value
      const getAttributeValue = (attributeType) => {
        const attr = attributes.find(a => a.AttributeType === attributeType);
        return attr ? attr.Value : 'false';
      };

      // Helper function to get storage config by bucket prefix pattern
      const getStorageByPrefix = (prefixPattern) => {
        // Define pattern variations for different storage types
        const patternVariations = {
          'CallRecordings': ['CallRecordings', 'call_recordings', 'callrecordings', 'CallRecording', 'call_recording'],
          'ChatTranscripts': ['ChatTranscripts', 'chat_transcripts', 'chattranscripts', 'ChatTranscript', 'chat_transcript'],
          'Reports': ['Reports', 'reports', 'Report', 'report'],
          'Attachments': ['Attachments', 'attachments', 'Attachment', 'attachment']
        };

        // Get all possible patterns for this storage type
        const patterns = patternVariations[prefixPattern] || [prefixPattern];

        // Search for storage config that matches any of the patterns (case-insensitive)
        return storageConfigs.find(s => {
          if (s.StorageType !== 'S3' || !s.S3Config?.BucketPrefix) {
            return false;
          }

          const bucketPrefix = s.S3Config.BucketPrefix.toLowerCase();
          return patterns.some(pattern =>
            bucketPrefix.includes(pattern.toLowerCase())
          );
        });
      };

      // Extract AWS Account ID from ARN
      const accountId = details.Arn ? details.Arn.split(':')[4] : 'N/A';

      // Determine identity provider based on identity management type
      const getIdentityProvider = () => {
        switch (details.IdentityManagementType) {
          case 'SAML':
            return 'SAML-based';
          case 'CONNECT_MANAGED':
            return 'Amazon Connect';
          case 'EXISTING_DIRECTORY':
            return 'AWS Directory Service';
          default:
            return details.IdentityManagementType || 'N/A';
        }
      };

      // Determine telephony options
      const getTelephonyOptions = () => {
        const inbound = details.InboundCallsEnabled;
        const outbound = details.OutboundCallsEnabled;
        
        if (inbound && outbound) return 'Allow Both Incoming and Outgoing Calls';
        if (inbound && !outbound) return 'Allow Incoming Calls Only';
        if (!inbound && outbound) return 'Allow Outgoing Calls Only';
        return 'No Calls Allowed';
      };

      // Get storage bucket name
      const getBucketName = (storageConfig) => {
        if (!storageConfig?.S3Config?.BucketName) return '<autogenerated>';
        return storageConfig.S3Config.BucketName;
      };

      // Get encryption type
      const getEncryptionType = (storageConfig) => {
        if (!storageConfig?.S3Config?.EncryptionConfig) return 'N/A';
        const encryptionType = storageConfig.S3Config.EncryptionConfig.EncryptionType;
        return encryptionType === 'KMS' ? 'aws/connect' : encryptionType;
      };

      // Get bucket prefix
      const getBucketPrefix = (storageConfig) => {
        if (!storageConfig?.S3Config?.BucketPrefix) return 'N/A';
        return storageConfig.S3Config.BucketPrefix;
      };

      // Storage configurations based on bucket prefix patterns
      const callRecordingConfig = getStorageByPrefix('CallRecordings');
      const chatTranscriptConfig = getStorageByPrefix('ChatTranscripts');
      const reportsConfig = getStorageByPrefix('Reports');
      const attachmentsConfig = getStorageByPrefix('Attachments');

      // Map all the fields from the screenshot
      const configData = {
        // AWS Account Section
        use: 'Existing account',
        accountName: accountId,

        // Amazon Connect Instance Section
        instanceAlias: details.InstanceAlias || 'N/A',
        instanceRegion: config.region || 'N/A',
        identityManagement: details.IdentityManagementType || 'N/A',
        directory: details.IdentityManagementType === 'EXISTING_DIRECTORY' ? 'AWS Directory Service' : 'N/A',
        identityProvider: getIdentityProvider(),
        administratorFirstName: 'N/A', // Not available in API for CONNECT_MANAGED
        administratorLastName: 'N/A',
        administratorUserName: 'N/A',
        password: 'N/A',
        accessUrl: details.InstanceAccessUrl || 'N/A',
        ccpUrl: details.InstanceAccessUrl ? `${details.InstanceAccessUrl}/ccp-v2` : 'N/A',
        telephonyOptions: getTelephonyOptions(),
        enableOutboundCampaigns: getAttributeValue('OUTBOUND_CAMPAIGNS') === 'true' ? 'Yes' : 'No',
        enableEarlyMedia: getAttributeValue('EARLY_MEDIA') === 'true' ? 'Yes' : 'No',
        enableMultiPartyCalls: getAttributeValue('MULTI_PARTY_CALLS') === 'true' ? 'Yes' : 'No',
        amazonPolly: getAttributeValue('AUTO_RESOLVE_BEST_VOICES') === 'true' ? 'Use the best available voice' : 'No',
        enableCustomerProfiles: integrations.some(i => i.IntegrationType === 'CUSTOMER_PROFILES') ? 'Yes' : 'No',
        enableCallRecordings: callRecordingConfig ? 'Yes' : 'No',
        enableContactLens: getAttributeValue('CONTACT_LENS') === 'true' ? 'Yes' : 'No',

        // Storage Configuration Section - Call Recordings
        callRecordingS3Bucket: callRecordingConfig ? getBucketName(callRecordingConfig) : 'N/A',
        callRecordingPathPrefix: callRecordingConfig ? getBucketPrefix(callRecordingConfig) : 'N/A',
        callRecordingEncryption: callRecordingConfig ? getEncryptionType(callRecordingConfig) : 'N/A',
        
        // Storage Configuration Section - Chat Transcripts
        enableChatTranscripts: chatTranscriptConfig ? 'Yes' : 'No',
        chatTranscriptS3Bucket: chatTranscriptConfig ? getBucketName(chatTranscriptConfig) : 'N/A',
        chatTranscriptPathPrefix: chatTranscriptConfig ? getBucketPrefix(chatTranscriptConfig) : 'N/A',
        chatTranscriptEncryption: chatTranscriptConfig ? getEncryptionType(chatTranscriptConfig) : 'N/A',
        
        // Storage Configuration Section - Exported Reports
        enableExportedReports: reportsConfig ? 'Yes' : 'No',
        exportedReportS3Bucket: reportsConfig ? getBucketName(reportsConfig) : 'N/A',
        exportedReportPathPrefix: reportsConfig ? getBucketPrefix(reportsConfig) : 'N/A',
        exportedReportEncryption: reportsConfig ? getEncryptionType(reportsConfig) : 'N/A',
        
        // Storage Configuration Section - Attachment Sharing
        enableAttachmentSharing: attachmentsConfig ? 'Yes' : 'No',
        attachmentS3Bucket: attachmentsConfig ? getBucketName(attachmentsConfig) : 'N/A',
        attachmentPathPrefix: attachmentsConfig ? getBucketPrefix(attachmentsConfig) : 'N/A',
        attachmentEncryption: attachmentsConfig ? getEncryptionType(attachmentsConfig) : 'N/A',
        
        contactFlowLogGroup: `aws/connect/${details.InstanceAlias || instanceId}`,
      };

      mappedData.push(configData);
    }
  });
  
  return mappedData;
};

// Helper function to get level key name
const getLevelKey = (levelNumber) => {
  const levelKeys = ['LevelOne', 'LevelTwo', 'LevelThree', 'LevelFour', 'LevelFive'];
  return levelKeys[levelNumber - 1];
};

// Helper function to create grouped hierarchy rows
const createGroupedHierarchyRows = (userHierarchyGroups) => {
  // Build a tree structure from the hierarchy groups
  const tree = buildHierarchyTree(userHierarchyGroups);
  
  // Convert tree to grouped rows
  const rows = [];
  traverseTreeForRows(tree, rows, []);
  
  return rows;
};

// Build a tree structure from user hierarchy groups
const buildHierarchyTree = (groups) => {
  const tree = {};
  
  // Sort groups by LevelId to process them in order
  const sortedGroups = [...groups].sort((a, b) => parseInt(a.LevelId) - parseInt(b.LevelId));
  
  sortedGroups.forEach(group => {
    const levelId = parseInt(group.LevelId);
    const hierarchyPath = group.HierarchyPath;
    
    // Build path from root to current node
    let currentLevel = tree;
    for (let i = 1; i <= levelId; i++) {
      const levelKey = getLevelKey(i);
      if (hierarchyPath[levelKey]) {
        const levelName = hierarchyPath[levelKey].Name;
        
        if (!currentLevel[levelName]) {
          currentLevel[levelName] = {
            name: levelName,
            level: i,
            children: {}
          };
        }
        currentLevel = currentLevel[levelName].children;
      }
    }
  });
  
  return tree;
};

// Traverse tree and create rows with full hierarchy path
const traverseTreeForRows = (tree, rows, parentPath) => {
  const entries = Object.entries(tree);
  
  entries.forEach(([nodeName, nodeData]) => {
    // Create row for current node
    const row = ['', '', '', '', ''];  // Initialize with empty strings
    
    // Fill in all parent levels for every row
    parentPath.forEach((parentName, parentIndex) => {
      row[parentIndex] = parentName;
    });
    
    // Fill in the current level
    const currentLevel = nodeData.level - 1;  // Convert to 0-based index
    row[currentLevel] = nodeName;
    
    rows.push(row);
    
    // Recurse for children
    if (Object.keys(nodeData.children).length > 0) {
      const newParentPath = [...parentPath];
      newParentPath[currentLevel] = nodeName;
      traverseTreeForRows(nodeData.children, rows, newParentPath);
    }
  });
};

const formatHoursString = (config) => {
  if (!config || !Array.isArray(config)) return '';
  
  const dayOrder = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  const dayAbbrev = {
    'MONDAY': 'M',
    'TUESDAY': 'T', 
    'WEDNESDAY': 'W',
    'THURSDAY': 'Th',
    'FRIDAY': 'F',
    'SATURDAY': 'Sa',
    'SUNDAY': 'Su'
  };
  
  const hoursStrings = [];
  
  dayOrder.forEach(day => {
    const dayConfig = config.find(c => c.Day === day);
    if (dayConfig) {
      const startTime = formatTime(dayConfig.StartTime);
      const endTime = formatTime(dayConfig.EndTime);
      
      // Check if it's a 24-hour day (00:00 to 00:00) or closed
      if (startTime === '12:00 AM' && endTime === '12:00 AM') {
        // This could be either 24/7 or closed - based on the sample data, it seems to be 24/7
        hoursStrings.push(`${dayAbbrev[day]} @ 24/7`);
      } else if (startTime && endTime) {
        hoursStrings.push(`${dayAbbrev[day]} @ ${startTime} - ${endTime}`);
      }
    }
  });
  
  return hoursStrings.join(', ');
};

const formatTime = (time) => {
  if (!time || (time.Hours === 0 && time.Minutes === 0)) {
    return '12:00 AM';
  }
  
  const hours = time.Hours;
  const minutes = time.Minutes;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, '0');
  
  return `${displayHours}:${displayMinutes} ${ampm}`;
};

export const mapUsers = (exportedData) => {
  const mappedData = [];
  
  // Iterate through each instance
  Object.entries(exportedData).forEach(([instanceId, instanceData]) => {
    // Get reference data for lookups
    const routingProfiles = instanceData.routingProfiles || [];
    const securityProfiles = instanceData.securityProfiles || [];
    // Extract userHierarchyGroups from the nested object structure
    const userHierarchyGroupsData = instanceData.userHierarchyGroups?.userHierarchyGroups || [];
    
    // Create lookup maps for performance
    const routingProfileMap = {};
    routingProfiles.forEach(profile => {
      routingProfileMap[profile.Id] = profile.Name;
    });
    
    const securityProfileMap = {};
    securityProfiles.forEach(profile => {
      securityProfileMap[profile.Id] = profile.Name;
    });
    
    // Create hierarchy lookup map
    const hierarchyMap = {};
    userHierarchyGroupsData.forEach(group => {
      hierarchyMap[group.Id] = group.HierarchyPath;
    });
    
    if (instanceData.users) {
      instanceData.users.forEach(user => {
        // Get routing profile name
        const routingProfileName = user.RoutingProfileId ? 
          routingProfileMap[user.RoutingProfileId] || user.RoutingProfileId : '';
        
        // Get security profile names (can be multiple)
        const securityProfileNames = user.SecurityProfileIds ? 
          user.SecurityProfileIds.map(id => securityProfileMap[id] || id).join(', ') : '';
        
        // Get hierarchy levels
        const hierarchy = user.HierarchyGroupId ? hierarchyMap[user.HierarchyGroupId] : null;
        const level1 = hierarchy?.LevelOne?.Name || '';
        const level2 = hierarchy?.LevelTwo?.Name || '';
        const level3 = hierarchy?.LevelThree?.Name || '';
        const level4 = hierarchy?.LevelFour?.Name || '';
        const level5 = hierarchy?.LevelFive?.Name || '';
        
        // Extract phone configuration
        const phoneConfig = user.PhoneConfig || {};
        const identityInfo = user.IdentityInfo || {};
        
        mappedData.push({
          firstName: identityInfo.FirstName || '',
          lastName: identityInfo.LastName || '',
          emailAddress: identityInfo.Email || '',
          userLogin: user.Username || '',
          routingProfile: routingProfileName,
          securityProfile: securityProfileNames,
          phoneType: phoneConfig.PhoneType || '',
          phoneNumber: phoneConfig.DeskPhoneNumber || '',
          softPhoneAutoAccept: phoneConfig.AutoAccept ? 'Yes' : 'No',
          acwTimeout: phoneConfig.AfterContactWorkTimeLimit || '',
          level1: level1,
          level2: level2,
          level3: level3,
          level4: level4,
          level5: level5
        });
      });
    }
  });
  
  return mappedData;
};